import * as React from 'react';
import { FC, useRef } from 'react';
import { makeStyles, createStyles } from '@material-ui/styles';
import Flickity, { FlickityOptions } from 'react-flickity-component';
import Image from './Image';
import stylesheet, { blue, bodyFontSize, backgroundColor } from 'src/constants/stylesheet';
import spacing from 'src/constants/spacing';
import prevIcon from '../assets/svgs/marinex_arrow_left_blue.svg';
import nextIcon from '../assets/svgs/marinex_arrow_right_blue.svg';
import mediaQueries from 'src/constants/responsive';
import PreviewItem from './PreviewItem';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
        },
        sliderContainer: {
            position: 'relative',
            overflow: 'hidden',
            marginBottom: spacing.Gutter.Desktop * 2,
            '@global': {
                '.flickity-button': {
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',

                    '&.next': {
                        right: 30,
                    },
                    '&.previous': {
                        left: 30,
                    }
                },
                '.flickity-prev-next-button': {
                    width: 50,
                    height: 50,
                },
                '.flickity-button-icon': {
                    fill: 'white',
                },
                '.flickity-button:disabled': {
                    display: 'none',
                },

                '.flickity-page-dots': {
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: '6%',
                    display: 'flex',
                    justifyContent: 'center',
                    listStyle: 'none',
                },
                '.flickity-page-dots .dot': {
                    margin: '0 5px',
                    width: 14,
                    height: 14,
                    opacity: 1,
                    background: 'white',
                    border: `1px solid ${blue}`,
                    borderRadius: '100%',
                    cursor: 'pointer',
                },
                '.flickity-page-dots .dot.is-selected': {
                    background: blue,
                }
            },
        },
        figure: {
            position: 'relative',
            height: 0,
            width: '100%',
            overflow: 'hidden',
            [mediaQueries.Mobile]: {
                height: 300
            },
            [mediaQueries.AfterMobile]: {
                height: 450
            }
        },
        figureLegend: {
            position: 'absolute',
            top: '50%',
            left: '0',
            right: '0',
            width: '80%',
            margin: '0 auto',
            transform: 'translateY(-50%)',
            ...stylesheet.TitleH1,
            color: 'white',
            textAlign: 'center',
            lineHeight: 1.1,
            textShadow: '0.1px 0.5px 3px #000',
        },
        image: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center'
        },
        NewsTitle: {
            ...stylesheet.TitleH1,
            textAlign: 'center',
            marginBottom: spacing.Gutter.BigDesktop * 2,
        },
        homeContent: {
            maxWidth: spacing.ContentMaxWidt,
            margin: '0 auto',
            boxSizing: 'content-box',
            [mediaQueries.Mobile]: {
                padding: '0 45px',
            },
            [mediaQueries.AfterMobile]: {
                padding: '0 60px',
            },
        },
        sectionSlider: {
            position: 'relative',
            marginBottom: 50,
            marginLeft: -spacing.Gutter.Desktop / 2,
            marginRight: -spacing.Gutter.Desktop / 2,
            '@global': {
                '.flickity-button': {
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',

                    '&.next': {
                        right: 0,
                        transform: 'translate(100%,-50%)',
                    },
                    '&.previous': {
                        left: 0,
                        transform: 'translate(100%,-50%)',
                    }
                },
                '.flickity-prev-next-button': {
                    width: 50,
                    height: 50,
                },
                '.flickity-button-icon': {
                    fill: blue,
                },
                '.flickity-button:disabled': {
                    display: 'none',
                },
            },
        },
        sectionSliderNextButton: {
            position: 'absolute',
            height: 35,
            top: '50%',
            right: -spacing.Gutter.Desktop / 2,
            transform: 'translate(100%, -50%)',

            '& img': {
                width: 'auto',
                height: '100%'
            }
        },
        sectionSliderPrevButton: {
            position: 'absolute',
            height: 35,
            top: '50%',
            left: -spacing.Gutter.Desktop / 2,
            transform: 'translate(-100%, -50%)',

            '& img': {
                width: 'auto',
                height: '100%'
            }
        },
        sectionSliderInner: {
            position: 'relative',
            overflow: 'hidden',
        },
        slideItem: {
            padding: '0 7.5px',
            boxSizing: 'border-box',

            [mediaQueries.Mobile]: {
                width: `100%`,
            },

            [mediaQueries.AfterMobile]: {
                width: `${1 / 3 * 100}%`,
            }
        },
        slideItemInner: {
            position: 'relative',
            width: '100%',
            height: '100%',
        },
        hr: {
            ...stylesheet.HR
        },
        slideItemFigure: {
            position: 'relative',
            height: 0,
            paddingBottom: '42.75%',
            overflow: 'hidden',
            marginBottom: 5,
            backgroundColor,
        },
        slideItemImage: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
        },
        coverImage: {
            objectFit: 'cover'
        },
        containImage: {
            objectFit: 'contain'
        },
        slideItemText: {
            position: 'relative',
            height: 160,

            '&:after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 1,
                height: 100,
                background: 'linear-gradient(0deg,#fff 7%,hsla(0,0%,100%,0))',
            },
        },
        slideItemTitle: {
            ...stylesheet.TitleH3
        },
        slideItemDate: {
            ...stylesheet.Condensed,
            color: 'black',
            [mediaQueries.Mobile]: {
                fontSize: `${12 / bodyFontSize.Mobile}rem`
            },
            [mediaQueries.Tablet]: {
                fontSize: `${12 / bodyFontSize.Tablet}rem`
            },
            [mediaQueries.Laptop]: {
                fontSize: `${12 / bodyFontSize.Laptop}rem`
            },
            [mediaQueries.Desktop]: {
                fontSize: `${12 / bodyFontSize.Desktop}rem`
            }
        },
        slideItemPreviewText: {},
    }));

export type HomeProps = HomeDto;
type Props = HomeProps;

const Home: FC<Props> = ({ Slides, NewsTitle, NewsItems, ProductsTitle, ProductsItems }) => {
    const cls = useStyles();
    const newsFlickity = useRef<any>(null);
    const productsFlickity = useRef<any>(null);
    // const [isMobile, setIsMobile] = useState<boolean>();

    // React.useEffect(() => {
    //     const checkMobile = () => {
    //         const r = window.innerWidth < 768;
    //         // tslint:disable-next-line:no-console
    //         console.log(`I am here!! It is ${r}`);

    //         setIsMobile(r);
    //     }

    //     window.addEventListener('resize', checkMobile);

    //     return (() => window.removeEventListener('resize', checkMobile));
    // }, []);

    const handleNewsNextSlide = () => {
        if (!newsFlickity.current) { return }
        newsFlickity.current.next();
    }

    const handleNewsPrevSlide = () => {
        if (!newsFlickity.current) { return }
        newsFlickity.current.previous();
    }

    const handleProductsNextSlide = () => {
        if (!productsFlickity.current) { return }
        productsFlickity.current.next();
    }

    const handleProductsPrevSlide = () => {
        if (!productsFlickity.current) { return }
        productsFlickity.current.previous();
    }

    const isMobile = window.innerWidth < 768;
    const flickityHero: FlickityOptions = {
        cellAlign: 'left',
        pageDots: Slides.length > 1,
        prevNextButtons: Slides.length > 1 && !isMobile
    }

    const flickitySection: FlickityOptions = {
        cellAlign: 'left',
        pageDots: false,
        prevNextButtons: false
    }

    return (
        <section className={cls.root}>
            <div className={cls.sliderContainer}>
                <Flickity
                    className={'carousel'} // default ''
                    elementType={'div'} // default 'div'
                    options={flickityHero} // takes flickity options {}
                    disableImagesLoaded={false} // default false
                >
                    {Slides.map((slide, i) =>
                        <figure
                            key={`figure-${i}`}
                            className={cls.figure}
                        >
                            <Image
                                className={cls.image}
                                pictureRef={slide.Image}
                                sizes={"100vw"}
                            />
                            {slide.Text &&
                                <figcaption
                                    className={cls.figureLegend}
                                    dangerouslySetInnerHTML={{ __html: slide.Text.replace("\n", "<br />") }} />
                            }
                        </figure>
                    )}
                </Flickity>
            </div>
            <section className={cls.homeContent}>

                {NewsItems.length > 0 &&
                    <>
                        {
                            NewsTitle &&
                            <h2 className={cls.NewsTitle}>{NewsTitle}</h2>
                        }

                        <div className={cls.sectionSlider}>
                            <button
                                className={cls.sectionSliderNextButton}
                                onClick={handleNewsNextSlide}
                            >
                                <img src={nextIcon} alt={""} />
                            </button>
                            <button
                                className={cls.sectionSliderPrevButton}
                                onClick={handleNewsPrevSlide}
                            >
                                <img src={prevIcon} alt={""} />
                            </button>
                            <div className={cls.sectionSliderInner}>
                                <Flickity
                                    className={'carousel'} // default ''
                                    elementType={'div'} // default 'div'
                                    options={flickitySection} // takes flickity options {}
                                    disableImagesLoaded={false} // default false
                                    // tslint:disable-next-line:jsx-no-lambda
                                    flickityRef={(r) => newsFlickity.current = r}
                                >
                                    {NewsItems.map((item, i) =>
                                        <div
                                            key={`item-${i}`}
                                            className={cls.slideItem}
                                        >
                                            <PreviewItem previewItem={item} />
                                        </div>
                                    )}
                                </Flickity>
                            </div>
                        </div>
                    </>}

                {ProductsItems.length > 0 &&
                    <>
                        {
                            ProductsTitle &&
                            <h2 className={cls.NewsTitle}>{ProductsTitle}</h2>
                        }
                        <div className={cls.sectionSlider}>
                            <button
                                className={cls.sectionSliderNextButton}
                                onClick={handleProductsNextSlide}
                            >
                                <img src={nextIcon} alt={""} />
                            </button>
                            <button
                                className={cls.sectionSliderPrevButton}
                                onClick={handleProductsPrevSlide}
                            >
                                <img src={prevIcon} alt={""} />
                            </button>
                            <div className={cls.sectionSliderInner}>
                                <Flickity
                                    className={'carousel'} // default ''
                                    elementType={'div'} // default 'div'
                                    options={flickitySection} // takes flickity options {}
                                    disableImagesLoaded={false} // default false
                                    // tslint:disable-next-line:jsx-no-lambda
                                    flickityRef={(r) => productsFlickity.current = r}
                                >
                                    {ProductsItems.map((item, i) =>
                                        <div
                                            key={`item-${i}`}
                                            className={cls.slideItem}
                                        >
                                            <PreviewItem previewItem={item} />
                                        </div>
                                    )}
                                </Flickity>
                            </div>
                        </div>
                    </>}
            </section>
        </section>
    );
}

export default Home;


// WEBPACK FOOTER //
// ./src/Components/Home.tsx